import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, LucideIcon } from "lucide-react";
import { buttonVariants } from "@/components/ui/button-variants";
import { cn } from "@/lib/utils";
import type { NavItem } from "@/config/navigation";
import { isNavHrefActive, isNavItemBranchActive } from "@/config/familyHubSidebarNav";

interface NavNestedSubmenuGroupProps {
  item: NavItem;
  isCollapsed: boolean;
  isMobile: boolean;
  onClick?: () => void;
  depth?: number;
}

function nestedLinkHeightClass(isMobile: boolean, isNestedLeaf: boolean) {
  if (isMobile) return "h-10 px-4 text-sm";
  if (isNestedLeaf) return "min-h-8 h-auto py-1.5 px-2 text-sm";
  return "h-9 px-3 text-sm";
}

/**
 * Nested Link Class.
 */
function nestedLinkClass(isActive: boolean, isMobile: boolean, depth: number) {
  const isNestedLeaf = depth > 0;

  return cn(
    buttonVariants({ variant: "ghost" }),
    "w-full justify-start font-medium transition-all duration-200 rounded-lg group",
    nestedLinkHeightClass(isMobile, isNestedLeaf),
    isActive
      ? "bg-muted text-foreground shadow-sm"
      : "text-foreground/70 hover:bg-muted/50 hover:text-foreground"
  );
}

function nestedIndentClass() {
  return "ml-2 pl-2";
}

function navIconGapClass(isMobile: boolean, isNestedLeaf: boolean) {
  if (isMobile) return "gap-3";
  return isNestedLeaf ? "gap-2" : "gap-2.5";
}

function navIconClass(isMobile: boolean, isNestedLeaf: boolean, extra?: string) {
  return cn(
    "flex-shrink-0 text-muted-foreground group-hover:text-foreground",
    isMobile ? "h-5 w-5" : "h-4 w-4",
    isNestedLeaf && "mt-0.5",
    extra
  );
}

/**
 * Nav Leaf Link.
 */
function NavLeafLink({
  href,
  icon: Icon,
  label,
  isMobile,
  depth,
  onClick,
}: Readonly<{
  href: string;
  icon: LucideIcon;
  label: string;
  isMobile: boolean;
  depth: number;
  onClick?: () => void;
}>) {
  const { pathname, search } = useLocation();
  const isNestedLeaf = depth > 0;

  return (
    <NavLink
      to={href}
      onClick={onClick}
      title={label}
      className={() =>
        cn(
          "flex w-full rounded-lg",
          isNestedLeaf ? "items-start" : "items-center",
          nestedLinkClass(isNavHrefActive(href, pathname, search), isMobile, depth),
          navIconGapClass(isMobile, isNestedLeaf)
        )
      }
    >
      <Icon className={navIconClass(isMobile, isNestedLeaf)} />
      <span
        className={cn(
          "min-w-0 flex-1",
          isNestedLeaf ? "whitespace-normal leading-snug text-left break-words" : "truncate"
        )}
      >
        {label}
      </span>
    </NavLink>
  );
}

/** Blog-style nested group inside a parent submenu (e.g. Finances → Goals). */
export function NavNestedSubmenuGroup({
  item,
  isCollapsed,
  isMobile,
  onClick,
  depth = 0,
}: Readonly<NavNestedSubmenuGroupProps>) {
  const { pathname, search } = useLocation();
  const hasNestedSubmenu = Boolean(item.submenu?.length);
  const branchActive = isNavItemBranchActive(item, pathname, search);
  const [isExpanded, setIsExpanded] = useState(branchActive);

  useEffect(() => {
    if (branchActive) {
      setIsExpanded(true);
    }
  }, [branchActive]);

  if (!hasNestedSubmenu) {
    return (
      <NavLeafLink
        href={item.href}
        icon={item.icon}
        label={item.title}
        isMobile={isMobile}
        depth={depth}
        onClick={onClick}
      />
    );
  }

  const Icon = item.icon;

  return (
    <div className="w-full">
      <div className="flex items-center w-full">
        <NavLink
          to={item.href}
          onClick={() => {
            setIsExpanded((prev) => !prev);
            onClick?.();
          }}
          title={item.title}
          className={() =>
            cn(
              "flex flex-1 items-center rounded-lg",
              nestedLinkClass(branchActive, isMobile, depth),
              navIconGapClass(isMobile, false)
            )
          }
        >
          <Icon
            className={navIconClass(isMobile, false, branchActive ? "text-primary" : undefined)}
          />
          <span className="truncate flex-1 text-left">{item.title}</span>
        </NavLink>
        {isCollapsed ? null : (
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsExpanded((prev) => !prev);
            }}
            aria-label={isExpanded ? `Collapse ${item.title}` : `Expand ${item.title}`}
            aria-expanded={isExpanded}
            className={cn(
              buttonVariants({ variant: "ghost", size: "icon" }),
              "h-9 w-9 p-0 flex-shrink-0",
              isExpanded && "text-primary"
            )}
          >
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform duration-200",
                isExpanded && "rotate-180"
              )}
            />
          </button>
        )}
      </div>
      {isExpanded && !isCollapsed ? (
        <div className={cn("mt-2 space-y-2 border-l border-border/50", nestedIndentClass())}>
          {item.submenu?.map((child) =>
            child.submenu?.length ? (
              <NavNestedSubmenuGroup
                key={child.href}
                item={child}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                onClick={onClick}
                depth={depth + 1}
              />
            ) : (
              <NavLeafLink
                key={child.href}
                href={child.href}
                icon={child.icon}
                label={child.title}
                isMobile={isMobile}
                depth={depth + 1}
                onClick={onClick}
              />
            )
          )}
        </div>
      ) : null}
    </div>
  );
}
